import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import type { TriageRuleWizardStep } from "./types";

type StepMeta = {
  key: TriageRuleWizardStep;
  title: string;
  subtitle: string;
  iconBg: string;
  iconColor: string;
  Icon: typeof BookmarkBorderOutlinedIcon;
};

const CONNECTOR_HEIGHT = 28;

type TriageRuleFlowSidebarProps = Readonly<{
  activeStep: TriageRuleWizardStep;
  onStepChange: (step: TriageRuleWizardStep) => void;
  stepSubtitles?: Partial<Record<TriageRuleWizardStep, string>>;
  disabledSteps?:
    | ReadonlySet<TriageRuleWizardStep>
    | readonly TriageRuleWizardStep[];
  invalidSteps?:
    | ReadonlySet<TriageRuleWizardStep>
    | readonly TriageRuleWizardStep[];
}>;

function isStepInList(
  step: TriageRuleWizardStep,
  list:
    | ReadonlySet<TriageRuleWizardStep>
    | readonly TriageRuleWizardStep[]
    | undefined,
): boolean {
  if (!list) return false;
  if (list instanceof Set) return list.has(step);
  return Array.isArray(list) && list.includes(step);
}

export default function TriageRuleFlowSidebar({
  activeStep,
  onStepChange,
  stepSubtitles,
  disabledSteps,
  invalidSteps,
}: TriageRuleFlowSidebarProps) {
  const steps: StepMeta[] = [
    {
      key: "when",
      title: "When: Auto-triaging runs",
      subtitle: stepSubtitles?.when ?? "",
      iconBg: "#E8F8EF",
      iconColor: "#22C55E",
      Icon: BookmarkBorderOutlinedIcon,
    },
    {
      key: "object",
      title: "If: Object meets the criteria",
      subtitle:
        stepSubtitles?.object ?? "Please select object type and sub-type",
      iconBg: "#FFF7E6",
      iconColor: "#FAAD14",
      Icon: TuneOutlinedIcon,
    },
    {
      key: "if",
      title: "If: Meet the condition(s)",
      subtitle: stepSubtitles?.if ?? "Please add condition(s)",
      iconBg: "#FFF7E6",
      iconColor: "#FAAD14",
      Icon: FilterAltOutlinedIcon,
    },
    {
      key: "then",
      title: "Then: Run this flow",
      subtitle: stepSubtitles?.then ?? "Please select a process flow",
      iconBg: "#EEF3FF",
      iconColor: "#295EF1",
      Icon: BoltOutlinedIcon,
    },
  ];

  return (
    <Box
      sx={{
        flex: "1 1 50%",
        width: "50%",
        minWidth: 0,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        alignSelf: "stretch",
        pt: 0,
        pb: 0,
        px: { xs: 2, md: 4, lg: 6 },
        bgcolor: "transparent",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 560,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {steps.map((step, index) => {
          const isSelectable = !isStepInList(step.key, disabledSteps);
          const isActive = isSelectable && step.key === activeStep;
          const Icon = step.Icon;
          const hasSubtitle = Boolean(step.subtitle);
          const showError = isStepInList(step.key, invalidSteps);
          const showConnectorBelow = index < steps.length - 1;

          return (
            <Box key={step.key} sx={{ width: "100%" }}>
              <Box
                component={isSelectable ? "button" : "div"}
                type={isSelectable ? "button" : undefined}
                aria-pressed={isSelectable ? isActive : undefined}
                aria-disabled={!isSelectable ? true : undefined}
                onClick={
                  isSelectable ? () => onStepChange(step.key) : undefined
                }
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  textAlign: "left",
                  border: showError
                    ? "2px solid #CF1322"
                    : isActive
                      ? "2px solid #295EF1"
                      : "2px solid transparent",
                  borderRadius: "12px",
                  bgcolor: "#fff",
                  boxShadow: isActive
                    ? "0 1px 3px rgba(0, 0, 0, 0.06)"
                    : "none",
                  p: 0,
                  cursor: isSelectable ? "pointer" : "not-allowed",
                  font: "inherit",
                  color: "inherit",
                  overflow: "hidden",
                  WebkitAppearance: "none",
                  appearance: "none",
                  opacity: isSelectable ? 1 : 0.55,
                  ...(isSelectable
                    ? {
                        "&:hover": {
                          borderColor: showError
                            ? "#CF1322"
                            : isActive
                              ? "#295EF1"
                              : "transparent",
                        },
                        "&:focus-visible": {
                          outline: showError
                            ? "2px solid #CF1322"
                            : "2px solid #295EF1",
                          outlineOffset: 2,
                        },
                      }
                    : null),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2.5,
                    py: 2.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                      bgcolor: step.iconBg,
                      color: step.iconColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 600,
                        color: showError ? "#CF1322" : "#262626",
                        lineHeight: 1.4,
                      }}
                    >
                      {step.title}
                    </Typography>
                    {hasSubtitle ? (
                      <Typography
                        sx={{
                          mt: 0.5,
                          fontSize: "13px",
                          color: showError ? "#CF1322" : "#8c8c8c",
                          lineHeight: 1.45,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {step.subtitle}
                      </Typography>
                    ) : null}
                  </Box>
                  <ChevronRightIcon
                    sx={{ fontSize: 20, color: "#BFBFBF", flexShrink: 0 }}
                  />
                </Box>
              </Box>
              {showConnectorBelow ? (
                <Box
                  sx={{
                    width: 2,
                    height: CONNECTOR_HEIGHT,
                    bgcolor: "#E5E7EB",
                    ml: "42px",
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
